import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Plus, Trash2, Pencil, Repeat } from "lucide-react";
import { toast } from "sonner";

/**
 * 🆕 Feb 2026 — Gestione lista causali di sostituzione polizza.
 * Elenco tenant-scoped. Le causali attive vengono mostrate nel dialog
 * "Sostituzione Polizza" (Policies.jsx) in un dropdown obbligatorio.
 */
export default function AgencyReplacementReasons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, code, description, active }
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/replacement-reasons");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ code: "", description: "", active: true, preserve_full_policy: false });
  const openEdit = (r) => setEditing({ id: r.id, code: r.code || "", description: r.description || "", active: r.active !== false, preserve_full_policy: !!r.preserve_full_policy });

  const save = async () => {
    const code = (editing?.code || "").trim();
    if (!code) {
      toast.error("Il codice della causale è obbligatorio");
      return;
    }
    if (code.length > 40) {
      toast.error("Codice troppo lungo (max 40 caratteri)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code,
        description: (editing.description || "").trim(),
        active: editing.active !== false,
        preserve_full_policy: !!editing.preserve_full_policy,
      };
      if (editing.id) {
        await api.put(`/replacement-reasons/${editing.id}`, payload);
        toast.success("Causale aggiornata");
      } else {
        await api.post("/replacement-reasons", payload);
        toast.success("Causale creata");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await api.delete(`/replacement-reasons/${deleteTarget.id}`);
      toast.success("Causale eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="agency-replacement-reasons-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Repeat className="w-6 h-6 text-blue-600" />
            Causali di sostituzione
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configura la lista delle motivazioni disponibili quando sostituisci una polizza (es. cambio veicolo, cambio compagnia, ecc.).
            Le causali <strong>attive</strong> compariranno nel selettore obbligatorio del dialog di conferma sostituzione.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reasons-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openNew} data-testid="reasons-new-btn">
            <Plus className="w-4 h-4 mr-1" />
            Nuova causale
          </Button>
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Codice</th>
              <th className="text-left px-4 py-3 font-semibold">Descrizione</th>
              <th className="text-center px-4 py-3 font-semibold">Copia integrale</th>
              <th className="text-center px-4 py-3 font-semibold">Stato</th>
              <th className="text-right px-4 py-3 font-semibold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-400">Caricamento…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-400" data-testid="reasons-empty">
                  Nessuna causale configurata. Clicca <strong>Nuova causale</strong> per iniziare.
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`reason-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-slate-800">{r.code}</td>
                <td className="px-4 py-3 text-slate-600">{r.description || <span className="italic text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-center">
                  {r.preserve_full_policy ? (
                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100" title="Sostituzione integrale: solo targa e n° polizza modificabili">Sì</Badge>
                  ) : (
                    <span className="text-xs text-slate-400">No</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {r.active !== false ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Attiva</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">Disattiva</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`reason-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" />
                      Modifica
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteTarget(r)} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid={`reason-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Elimina
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog crea/modifica */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="reason-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifica causale" : "Nuova causale di sostituzione"}</DialogTitle>
            <DialogDescription>
              Il codice identifica la causale. La descrizione è visibile nel selettore al momento della sostituzione.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="reason-code">Codice *</Label>
                <Input
                  id="reason-code"
                  value={editing.code}
                  onChange={(e) => setEditing((p) => ({ ...p, code: e.target.value }))}
                  maxLength={40}
                  placeholder="Es. CAMBIO_VEICOLO"
                  data-testid="reason-code-input"
                />
                <div className="text-xs text-slate-400 mt-1">Max 40 caratteri, univoco per agenzia.</div>
              </div>
              <div>
                <Label htmlFor="reason-desc">Descrizione</Label>
                <Textarea
                  id="reason-desc"
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Es. Il cliente ha sostituito il veicolo assicurato"
                  data-testid="reason-description-input"
                />
              </div>
              <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-slate-50">
                <div>
                  <div className="font-semibold text-sm">Causale attiva</div>
                  <div className="text-xs text-slate-500">Se disattivata, non compare nel selettore di sostituzione.</div>
                </div>
                <Switch
                  checked={editing.active !== false}
                  onCheckedChange={(v) => setEditing((p) => ({ ...p, active: !!v }))}
                  data-testid="reason-active-switch"
                />
              </div>
              <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-amber-50 border-amber-200">
                <div className="flex-1 pr-3">
                  <div className="font-semibold text-sm text-amber-900">Copia integrale della polizza</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Se attivo, la sostituzione con questa causale (es. <em>cambio targhe</em>, <em>sostituzione veicolo</em>) crea una copia <strong>identica</strong> della polizza originale — vengono mantenute date, importi, garanzie, rate — e solo <strong>numero polizza</strong> e <strong>targa</strong> restano da compilare.
                  </div>
                </div>
                <Switch
                  checked={!!editing.preserve_full_policy}
                  onCheckedChange={(v) => setEditing((p) => ({ ...p, preserve_full_policy: !!v }))}
                  data-testid="reason-preserve-switch"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="reason-cancel-btn">Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="reason-save-btn">
              {saving ? "Salvataggio…" : (editing?.id ? "Salva modifiche" : "Crea causale")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog eliminazione */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="reason-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Elimina causale</DialogTitle>
            <DialogDescription>
              Vuoi eliminare la causale <strong>{deleteTarget?.code}</strong>? L&apos;operazione è irreversibile.
              <br />Le polizze già sostituite in passato con questa causale <strong>manterranno</strong> il riferimento storico.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={saving} data-testid="reason-delete-cancel-btn">Annulla</Button>
            <Button onClick={confirmDelete} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reason-delete-confirm-btn">
              {saving ? "Eliminazione…" : "Sì, elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
